/**
 * Add to Cart Modal JS - Premium Version
 * Intercepts AJAX ATC calls and displays a custom modal matching the mockup.
 *
 * FIXES:
 * - Interceptor now handles fetch() called with a Request object (not just string URL)
 * - Interceptor now handles URLSearchParams / FormData bodies in fetch
 * - XHR interceptor captures POST body to detect ATC calls more reliably
 * - updateItemQuantity() now also syncs the linked "Need It Faster" fee
 *   line item to the same quantity as the main product, using
 *   /cart/update.js instead of /cart/change.js so both line items can be
 *   updated atomically in a single request.
 * - updateItemQuantity() now respects the per-product quantity step
 *   (Quantity metafield) forwarded from the PDP via the hidden
 *   "_Quantity Step" line item property, instead of always stepping by 1.
 *   This makes the modal's +/- match the PDP and Cart page behavior.
 */

(function () {
  'use strict';

  const ATC_MODAL = {
    settings: {
      endpoints: ['/cart/add.js', '/cart/add'],
      cartEndpoint: '/cart.js',
      recommendationsEndpoint: '/recommendations/products.json'
    },
    
    init() {
      this.cacheDom();
      this.bindEvents();
      this.interceptRequests();
    },

    cacheDom() {
      this.$modal = document.getElementById('atc-modal');
      this.$image = document.getElementById('atc-product-image');
      this.$title = document.getElementById('atc-product-title');
      this.$sku = document.getElementById('atc-product-sku');
      this.$variant = document.getElementById('atc-product-variant');
      this.$qty = document.getElementById('atc-product-qty');
      this.$price = document.getElementById('atc-product-price');
      this.$cartCount = document.getElementById('atc-cart-count');
      this.$cartTotal = document.getElementById('atc-cart-total');
      this.$qtyPlus = document.getElementById('atc-qty-plus');
      this.$qtyMinus = document.getElementById('atc-qty-minus');
      this.$recommendationsContainer = document.getElementById('atc-recommendations-container');
      this.$pillsContainer = document.getElementById('atc-compatible-pills');
      this.$sliderPrev = document.getElementById('atc-slider-prev');
      this.$sliderNext = document.getElementById('atc-slider-next');

      this.currentSlideIndex = 0;
      this.currentRelatedCollections = [];
    },

    bindEvents() {
      // Prevent theme mini cart from opening when our modal is active
document.documentElement.addEventListener('product:added', (e) => {
  if (document.body.classList.contains('atc-modal-open')) {
    e.stopImmediatePropagation();
  }
}, true);
      document.addEventListener('click', (e) => {
        if (e.target.closest('[data-atc-close]')) {
          this.close();
        }

        // Handle Recommendation Add to Cart
        const recBtn = e.target.closest('.atc-modal__rec-btn');
        if (recBtn) {
          const variantId = recBtn.dataset.variantId;
          const card = recBtn.closest('.atc-modal__rec-card');
          const qtyInput = card.querySelector('.atc-modal__rec-qty-input');
          const qty = qtyInput.value || 1;

          this.addToCart(variantId, qty);

          const originalText = recBtn.textContent;
          recBtn.textContent = 'Adding...';
          recBtn.disabled = true;
          setTimeout(() => {
            recBtn.textContent = 'Added!';
            setTimeout(() => {
              recBtn.textContent = originalText;
              recBtn.disabled = false;
            }, 2000);
          }, 600);
        }
      });

      // Capture related products from the form on submit or button click
      const captureRelatedData = (el) => {
        const form = el.closest('form[action="/cart/add"], form[action*="/cart/add"]');
        if (form) {
          const relatedInput = form.querySelector('.atc-related-products');
          this.pendingRelatedHandles = relatedInput && relatedInput.value
            ? relatedInput.value.split(',').map(h => h.trim()).filter(h => h)
            : [];
        }
      };

      document.addEventListener('submit', (e) => {
        captureRelatedData(e.target);
      }, true);

      document.addEventListener('click', (e) => {
        const atcBtn = e.target.closest('[name="add"], .product-form__add-button, .product-item-new-add-to-cart button');
        if (atcBtn) captureRelatedData(atcBtn);
      }, true);

      if (this.$sliderPrev) {
        this.$sliderPrev.addEventListener('click', () => this.moveSlider('prev'));
      }
      if (this.$sliderNext) {
        this.$sliderNext.addEventListener('click', () => this.moveSlider('next'));
      }

      if (this.$qtyPlus) {
        this.$qtyPlus.addEventListener('click', () => this.updateItemQuantity('plus'));
      }
      if (this.$qtyMinus) {
        this.$qtyMinus.addEventListener('click', () => this.updateItemQuantity('minus'));
      }

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.$modal && this.$modal.getAttribute('aria-hidden') === 'false') {
          this.close();
        }
      });
    },

    /**
     * Resolve a fetch() input (string, URL object, or Request) to a plain URL string.
     */
    _resolveUrl(input) {
      if (typeof input === 'string') return input;
      if (input instanceof URL) return input.pathname + input.search;
      if (typeof Request !== 'undefined' && input instanceof Request) return input.url;
      return '';
    },

    /**
     * Check whether a URL string matches one of the ATC endpoints.
     */
    _isAtcUrl(urlStr) {
      try {
        // Handle both absolute and relative URLs
        const pathname = urlStr.startsWith('http') ? new URL(urlStr).pathname : urlStr.split('?')[0];
        return this.settings.endpoints.some(end => pathname === end || pathname.endsWith(end));
      } catch (e) {
        return this.settings.endpoints.some(end => urlStr.includes(end));
      }
    },

    interceptRequests() {
      const self = this;

      /* ---- fetch() interception ---- */
      const originalFetch = window.fetch;
      window.fetch = function (input, init) {
        const urlStr = self._resolveUrl(input);
        const isATC = self._isAtcUrl(urlStr);

        if (isATC) {
          return originalFetch.apply(this, arguments).then(response => {
            if (response.ok) {
              response.clone().json().then(data => self.handleSuccess(data)).catch(() => {});
            }
            return response;
          });
        }
        return originalFetch.apply(this, arguments);
      };

      /* ---- XMLHttpRequest interception ---- */
      const originalOpen = XMLHttpRequest.prototype.open;
      XMLHttpRequest.prototype.open = function (method, url) {
        this._atcMethod = (method || '').toUpperCase();
        this._atcUrl = url || '';
        return originalOpen.apply(this, arguments);
      };

      const originalSend = XMLHttpRequest.prototype.send;
      XMLHttpRequest.prototype.send = function (body) {
        const isATC = self._isAtcUrl(this._atcUrl || '');

        if (isATC) {
          this.addEventListener('load', function () {
            if (this.status >= 200 && this.status < 300) {
              try {
                const data = JSON.parse(this.responseText);
                self.handleSuccess(data);
              } catch (e) { /* non-JSON response, ignore */ }
            }
          });
        }
        return originalSend.apply(this, arguments);
      };
    },

    handleSuccess(data) {
      if (!data) return;

      // data can be a single item object OR { items: [...] } (cart response)
      const item = Array.isArray(data.items)
        ? data.items[0]
        : (data.items ? data.items[0] : data);

      // Guard: if we can't identify a product, bail silently
      if (!item || (!item.product_id && !item.title)) return;

      this.currentItem = item;
      this.currentRelatedHandles = this.pendingRelatedHandles || [];
      this.pendingRelatedHandles = [];

      // Failsafe: try finding related handles on the page by product ID
      if (this.currentRelatedHandles.length === 0 && item.product_id) {
        const pageData = document.querySelector(`.atc-related-products[data-product-id="${item.product_id}"]`);
        if (pageData && pageData.value) {
          this.currentRelatedHandles = pageData.value.split(',').filter(h => h.trim());
        }
      }

      // Related collections (optional metafield)
      this.currentRelatedCollections = [];
      const productId = item.product_id || '';
      const handle = item.handle || '';
      const colDataInput =
        document.querySelector(`.atc-related-collections[data-product-id="${productId}"]`) ||
        document.querySelector(`.atc-related-collections[data-product-handle="${handle}"]`);

      if (colDataInput && colDataInput.value) {
        this.currentRelatedCollections = colDataInput.value
          .split('|')
          .filter(Boolean)
          .map(str => {
            const parts = str.split(':');
            return parts.length >= 2 ? { handle: parts[0].trim(), title: parts[1].trim() } : null;
          })
          .filter(Boolean);
      }

      this.updateModalContent(item);
      this.fetchCartData();
      this.fetchRecommendations(item.product_id);

      // Reset slider
      this.currentSlideIndex = 0;
      if (this.$recommendationsContainer) {
        this.$recommendationsContainer.style.transform = 'translateX(0)';
      }
      if (this.$sliderPrev) this.$sliderPrev.disabled = true;

      this.open();

      document.documentElement.dispatchEvent(new CustomEvent('cart:updated', {
  bubbles: true,
  detail: { source: 'atc-modal' }
}));
    },

    updateModalContent(item) {
      if (!item) return;
      this.$image.src = item.image || item.featured_image || '';
      this.$image.alt = item.product_title || item.title || '';
      this.$title.textContent = item.product_title || item.title || '';
      this.$sku.textContent = item.sku || 'N/A';
      this.$variant.textContent =
        item.variant_title !== 'Default Title' && item.variant_title ? item.variant_title : '';
      this.$qty.textContent = item.quantity || 1;
      this.$price.textContent = this.formatMoney(item.price || item.original_price);

      // FIX: clear previous values so an old lead time / faster info never sticks
      ['#atc-product-lead-time', '#atc-product-additional-price', '#atc-product-faster-checkout'].forEach(function (sel) {
        var el = document.querySelector(sel);
        if (el) el.textContent = '';
      });

      let leadTime = item?.properties?.["Lead Time"];
      const additionalPrice = item?.properties?.["Additional price for need it faster"];
      const fasterCheckout = item?.properties?.["Faster checkout"];

      console.log("modal opening");

      console.log(item)

      console.log(additionalPrice);
      console.log(fasterCheckout);

      console.log(item.properties);
      console.log(leadTime)
      if (leadTime) {
        console.log("shoudld not be")
       document.querySelector("#atc-product-lead-time").textContent = `Lead time : ${leadTime}`;
      }
      if (additionalPrice) {
  document.querySelector("#atc-product-additional-price").textContent =
    `Additional Price: ${additionalPrice}`;
}

if (fasterCheckout) {
  document.querySelector("#atc-product-faster-checkout").textContent =
    `Faster Checkout: ${fasterCheckout}`;
}
      
    },

    /**
     * Read the quantity step for the current item.
     *
     * The ATC modal SNIPPET (rendered in the product page's Liquid
     * context) outputs a hidden input #atc-modal-quantity-step whose
     * value is the CURRENT PAGE product's Quantity metafield (e.g. 3),
     * along with data-product-id so we can confirm it actually belongs
     * to the product that was just added to cart.
     *
     * We read that here instead of touching the PDP quantity-selector
     * markup, so the modal's +/- buttons step by the same amount as the
     * PDP and Cart page (e.g. 3, 6, 9, 12...) instead of always moving
     * by 1.
     *
     * Falls back to 1 if the hidden input is missing, its value is
     * invalid, or its product id doesn't match the added item (e.g. the
     * modal was triggered from a different product's card on a
     * collection page).
     */
    getQuantityStep(item) {
      const stepInput = document.getElementById('atc-modal-quantity-step');
      if (!stepInput) return 1;

      const inputProductId = stepInput.dataset.productId;
      const itemProductId = item?.product_id ? String(item.product_id) : null;

      // Only trust the step value if it belongs to the product that was
      // actually added (guards against mismatches on collection/grid pages).
      if (inputProductId && itemProductId && inputProductId !== itemProductId) {
        return 1;
      }

      const step = parseInt(stepInput.value, 10);
      return (!isNaN(step) && step > 0) ? step : 1;
    },

    updateItemQuantity(action) {
      if (!this.currentItem) return;

      // Step-aware quantity change, matching the PDP/Cart page logic
      // driven by the product's Quantity metafield (read from the
      // modal snippet's hidden #atc-modal-quantity-step input).
      const step = this.getQuantityStep(this.currentItem);

      let qty = parseInt(this.$qty.textContent, 10);
      if (isNaN(qty)) qty = step;

      if (action === 'plus') {
        qty += step;
      }
      if (action === 'minus') {
        qty -= step;
        // Never drop below one full step (mirrors PDP's min="{{ qty_metafield }}" behavior)
        if (qty < step) qty = step;
      }

      this.$qty.textContent = qty;

      const mainKey = this.currentItem.key || this.currentItem.id;
      const fasterCompositeId = this.currentItem?.properties?.['Faster checkout'];

      // FIX: if this item has a linked "Need It Faster" fee line, look up
      // that line item's key in the live cart so we can sync its quantity
      // too. Without this, the modal's +/- only ever updated the main
      // product, leaving the fee line stuck at its old quantity - same
      // bug as on the cart page.
      //
      // IMPORTANT: match by the shared 'Faster checkout' PROPERTY VALUE,
      // not by variant_id. The fee-variant's underlying Shopify variant id
      // can be shared across different products (e.g. two products with
      // the same Need-It-Faster price both reuse the same fee variant),
      // so variant_id alone isn't unique enough to identify THIS
      // product's own fee line - only the composite property value
      // (set identically on both the main line and its own fee line) is.
      const findFeeLineKey = fasterCompositeId
        ? fetch('/cart.js')
            .then((res) => res.json())
            .then((cart) => {
              const feeLine = cart.items.find(
                (i) =>
                  i.key !== mainKey &&
                  i.properties &&
                  i.properties['Faster checkout'] === fasterCompositeId
              );
              return feeLine ? feeLine.key : null;
            })
            .catch(() => null)
        : Promise.resolve(null);

      findFeeLineKey
        .then((feeKey) => {
          const updates = {};
          updates[mainKey] = qty;
          if (feeKey) updates[feeKey] = qty; // keep fee line in sync

          // Use /cart/update.js (accepts a map of line-item-key -> quantity)
          // instead of /cart/change.js (single line only), so both lines
          // update atomically in one request.
          return fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ updates: updates })
          });
        })
        .then((res) => res.json())
        .then((cart) => {
          this.fetchCartData();
          document.documentElement.dispatchEvent(new CustomEvent('cart:updated', {
            bubbles: true,
            detail: { cart }
          }));
        })
        .catch((err) => console.error('ATC Modal: Error updating quantity', err));
    },

    renderCollectionPills(productType) {
      if (!this.$pillsContainer) return;

      if (this.currentRelatedCollections && this.currentRelatedCollections.length > 0) {
        // Hide static pills, add dynamic ones
        this.$pillsContainer.querySelectorAll('.atc-modal__pill-btn:not(.atc-modal__pill-dynamic)').forEach(p => p.style.display = 'none');
        this.$pillsContainer.querySelectorAll('.atc-modal__pill-dynamic').forEach(p => p.remove());

        this.currentRelatedCollections.forEach(col => {
          const pill = document.createElement('a');
          pill.href = `/collections/${col.handle}`;
          pill.className = 'atc-modal__pill-btn atc-modal__pill-dynamic';
          pill.style.display = 'inline-flex';
          pill.textContent = col.title;
          this.$pillsContainer.appendChild(pill);
        });
      } else {
        // Show all static pills
        this.$pillsContainer.querySelectorAll('.atc-modal__pill-dynamic').forEach(p => p.remove());
        this.$pillsContainer.querySelectorAll('.atc-modal__pill-btn').forEach(p => p.style.display = 'inline-flex');
      }
    },

    fetchCartData() {
      fetch(this.settings.cartEndpoint)
        .then(res => res.json())
        .then(cart => {
          if (this.$cartCount) this.$cartCount.textContent = cart.item_count;
          if (this.$cartTotal) this.$cartTotal.textContent = this.formatMoney(cart.total_price);
          document.querySelectorAll('.header__cart-count').forEach(el => {
            el.textContent = cart.item_count;
          });
        });
    },

    fetchRecommendations(productId) {
      const targetCount = 12;
      const metafieldHandles = (this.currentRelatedHandles || []).slice(0, targetCount);
      const finish = this.getFinishFromItem(this.currentItem);

      const metafieldPromise = metafieldHandles.length > 0
        ? this.fetchSpecificProducts(metafieldHandles, finish)
        : Promise.resolve([]);

      const recommendationUrl = `${this.settings.recommendationsEndpoint}?product_id=${productId}&limit=${targetCount}&intent=related`;
      const fallbackPromise = fetch(recommendationUrl)
        .then(res => res.json())
        .catch(() => ({ products: [] }));

      Promise.all([metafieldPromise, fallbackPromise])
        .then(([metafieldProducts, recommendationData]) => {
          const recommendationProducts = recommendationData.products || [];
          let combined = [...metafieldProducts].slice(0, targetCount);
          const existingIds = new Set(combined.map(p => p.id));
          existingIds.add(parseInt(productId));

          for (const rec of recommendationProducts) {
            if (combined.length >= targetCount) break;
            if (!existingIds.has(rec.id)) {
              combined.push(rec);
              existingIds.add(rec.id);
            }
          }

          this.renderRecommendations(combined);
        })
        .catch(err => console.error('ATC Modal: Error merging recommendations', err));
    },

    fetchSpecificProducts(handles, finish) {
      const promises = handles.map(handle =>
        fetch(`/products/${handle}.js`)
          .then(res => res.ok ? res.json() : null)
          .catch(() => null)
      );
      return Promise.all(promises).then(results =>
        results.filter(p => p !== null).map(product => this.findMatchingVariant(product, finish))
      );
    },

    getFinishFromItem(item) {
      if (!item) return '';
      if (item.options_with_values) {
        const finishOpt = item.options_with_values.find(opt =>
          ['finish', 'color', 'colour', 'finish/color', 'surface finish'].includes(opt.name.toLowerCase())
        );
        if (finishOpt) return finishOpt.value;
      }
      if (item.variant_options) {
        const possibleFinish = item.variant_options.find(opt => opt !== 'Default Title');
        if (possibleFinish) return possibleFinish;
      }
      if (item.variant_title && item.variant_title !== 'Default Title') return item.variant_title;
      return '';
    },

    findMatchingVariant(product, targetFinish) {
      if (!targetFinish || !product.variants) return product;
      const targetLower = targetFinish.toLowerCase().trim();

      let bestMatch = product.variants.find(variant =>
        variant.options.some(opt => {
          if (!opt) return false;
          const optLower = opt.toLowerCase().trim();
          return optLower === targetLower || targetLower.includes(optLower) || optLower.includes(targetLower);
        })
      );
      if (!bestMatch) bestMatch = product.variants.find(v => v.title && v.title.toLowerCase().includes(targetLower));
      if (!bestMatch) bestMatch = product.variants[0];

      return {
        ...product,
        featured_image: bestMatch.featured_image ? bestMatch.featured_image.src : (product.featured_image || ''),
        price: bestMatch.price,
        variants: [bestMatch]
      };
    },

    renderRecommendations(products) {
      this.$recommendationsContainer.innerHTML = '';
      if (!products || products.length === 0) {
        if (this.$sliderPrev) this.$sliderPrev.style.display = 'none';
        if (this.$sliderNext) this.$sliderNext.style.display = 'none';
        return;
      }

      const viewport = this.$recommendationsContainer.parentElement;
      let viewportWidth = viewport.offsetWidth;
      if (viewportWidth <= 0) viewportWidth = Math.min(window.innerWidth - 80, 1250);

      const gap = 12;
      const cardWidth = Math.floor((viewportWidth - (gap * 3)) / 4);

      products.forEach(product => {
        const variant = product.variants[0];
        const itemHtml = `
          <div class="atc-modal__rec-card" style="width: ${cardWidth}px; flex: 0 0 ${cardWidth}px;">
            <div class="atc-modal__rec-card-top">
              <img src="${product.featured_image}" alt="${product.title}" class="atc-modal__rec-img" loading="lazy">
              <div class="atc-modal__rec-details">
                <a href="/products/${product.handle}" class="atc-modal__rec-title">${product.title}</a>
                <p class="atc-modal__rec-sku">SKU: <span class="atc-modal__font-semibold">${variant.sku || 'N/A'}</span></p>
              </div>
            </div>
            <div class="atc-modal__rec-card-bottom">
              <p class="atc-modal__rec-price-label">Your Price</p>
              <p class="atc-modal__rec-price">${this.formatMoney(variant.price)}</p>
              <div class="atc-modal__rec-actions">
                <div class="atc-modal__rec-qty-box">
                  <span class="atc-modal__rec-qty-label">Qty</span>
                  <input type="number" value="1" min="1" class="atc-modal__rec-qty-input">
                  <div class="atc-modal__rec-qty-controls">
                    <button type="button" class="atc-modal__rec-qty-arrow atc-rec-plus">
                      <svg width="8" height="6" viewBox="0 0 10 10" fill="currentColor"><path d="M5 0L10 8H0L5 0Z"/></svg>
                    </button>
                    <button type="button" class="atc-modal__rec-qty-arrow atc-rec-minus">
                      <svg width="8" height="6" viewBox="0 0 10 10" fill="currentColor"><path d="M5 10L0 2H10L5 10Z"/></svg>
                    </button>
                  </div>
                </div>
                <button type="button" class="atc-modal__rec-btn" data-variant-id="${variant.id}">Add to Cart</button>
              </div>
            </div>
          </div>
        `;
        this.$recommendationsContainer.insertAdjacentHTML('beforeend', itemHtml);
      });

      this.renderCollectionPills(this.currentItem.product_type);
      this.initRecEventListeners();

      const showSlider = products.length > 4;
      if (this.$sliderPrev) {
        this.$sliderPrev.style.display = showSlider ? 'flex' : 'none';
        this.$sliderPrev.disabled = true;
      }
      if (this.$sliderNext) {
        this.$sliderNext.style.display = showSlider ? 'flex' : 'none';
        this.$sliderNext.disabled = !showSlider;
      }
    },

    initRecEventListeners() {
      this.$recommendationsContainer.querySelectorAll('.atc-modal__rec-btn').forEach(btn => {
        btn.onclick = (e) => {
          const variantId = e.target.dataset.variantId;
          const qty = e.target.closest('.atc-modal__rec-card').querySelector('.atc-modal__rec-qty-input').value;
          this.addToCart(variantId, parseInt(qty) || 1);
        };
      });
      this.$recommendationsContainer.querySelectorAll('.atc-rec-plus').forEach(btn => {
        btn.onclick = (e) => {
          const input = e.currentTarget.closest('.atc-modal__rec-qty-box').querySelector('.atc-modal__rec-qty-input');
          input.value = parseInt(input.value) + 1;
        };
      });
      this.$recommendationsContainer.querySelectorAll('.atc-rec-minus').forEach(btn => {
        btn.onclick = (e) => {
          const input = e.currentTarget.closest('.atc-modal__rec-qty-box').querySelector('.atc-modal__rec-qty-input');
          if (parseInt(input.value) > 1) input.value = parseInt(input.value) - 1;
        };
      });
    },

    moveSlider(direction) {
      const cards = this.$recommendationsContainer.children;
      if (cards.length === 0) return;
      const gap = 12;
      const cardWidth = cards[0].offsetWidth;
      const itemsPerView = 4;
      const maxIndex = Math.max(0, cards.length - itemsPerView);

      if (direction === 'next' && this.currentSlideIndex < maxIndex) this.currentSlideIndex++;
      else if (direction === 'prev' && this.currentSlideIndex > 0) this.currentSlideIndex--;

      const offset = this.currentSlideIndex * (cardWidth + gap);
      this.$recommendationsContainer.style.transform = `translateX(-${offset}px)`;
      if (this.$sliderPrev) this.$sliderPrev.disabled = (this.currentSlideIndex === 0);
      if (this.$sliderNext) this.$sliderNext.disabled = (this.currentSlideIndex >= maxIndex);
    },

    addToCart(variantId, qty) {
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: qty })
      })
        .then(res => res.json())
        .then(() => {
          this.fetchCartData();
         document.documentElement.dispatchEvent(new CustomEvent('cart:updated', {
  bubbles: true,
  detail: { source: 'atc-modal' }
}));
        })
        .catch(err => console.error('ATC Modal: Error adding rec product', err));
    },

    formatMoney(cents) {
      if (typeof cents === 'string') return cents;
      const format = window.theme.moneyWithCurrencyFormat || window.theme.moneyFormat || '$ {{amount}}';
      const amount = (cents / 100).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
      return format
        .replace(/\{\{\s*amount\s*\}\}/, amount)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100));
    },

    open() {
      if (!this.$modal) return;
      this.$modal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('atc-modal-open');
    },

    close() {
      if (!this.$modal) return;
      this.$modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('atc-modal-open');
    }
  };

  if (document.readyState === 'complete') {
  ATC_MODAL.init();
} else {
  window.addEventListener('load', () => ATC_MODAL.init());
}
})();
